import { apiFetch } from '@/shared/api/client'
import {
  getOrCreateTodayTrip,
  saveTripRecord,
  todayDateKey,
  type DeliveryLine,
  type DeliveryStop,
  type DeliveryStopStatus,
  type ReceivingStatus,
  type ReturnPickupDoc,
  type TripRecord,
  type TripStatus,
} from '@/features/delman/storage/tripStorage'
import { shouldUseDelmanMock } from '@/features/delman/api/delmanApiMode'

type ApiDeliveryLine = {
  product_name: string
  production_date: string
  qty_shipped: number
  qty_received?: number
}

type ApiDelivery = {
  id: string
  store_name: string
  sort_order: number
  status: string
  receiving_status?: string
  lines: ApiDeliveryLine[]
  qr_shown_at?: string
  receiving_draft_at?: string
}

type ApiReturnLine = {
  product_name: string
  production_date: string
  qty: number
}

type ApiReturnPickup = {
  id: string
  return_code: string
  store_name: string
  item_count: number
  destination: 'to_kitchen'
  status: 'waiting_scan' | 'picked_up'
  picked_up_at?: string
  lines?: ApiReturnLine[]
}

export type ApiDeliveryTrip = {
  id: string
  delman_name: string
  kitchen_name: string
  date: string
  status: TripStatus
  started_at?: string
  completed_at?: string
  transit_remainder_qty?: number
  started_via_delivery_id?: string
  deliveries: ApiDelivery[]
  return_pickups?: ApiReturnPickup[]
}

export type TodaySessionsLoad = {
  active: TripRecord
  completedToday: TripRecord[]
  /** true jika gagal memuat dari API (offline / server error). */
  apiError?: boolean
}

function mapReceivingStatus(value?: string): ReceivingStatus {
  if (value === 'draft') return 'draft'
  if (value === 'completed') return 'completed'
  return 'none'
}

function mapStopStatus(delivery: ApiDelivery): DeliveryStopStatus {
  const receiving = mapReceivingStatus(delivery.receiving_status)
  if (receiving === 'completed' || delivery.status === 'receiving_completed') {
    return 'receiving_completed'
  }
  if (receiving === 'draft' || delivery.status === 'receiving_draft') {
    return 'receiving_draft'
  }
  if (delivery.qr_shown_at || delivery.status === 'qr_shown') {
    return 'qr_shown'
  }
  return 'pending'
}

function mapDeliveryLine(line: ApiDeliveryLine): DeliveryLine {
  return {
    productName: line.product_name,
    productionDate: line.production_date,
    qtyShipped: line.qty_shipped,
    qtyReceived: line.qty_received,
  }
}

function mapDeliveryStop(delivery: ApiDelivery): DeliveryStop {
  const receivingStatus = mapReceivingStatus(delivery.receiving_status)
  return {
    id: delivery.id,
    storeName: delivery.store_name,
    order: delivery.sort_order,
    status: mapStopStatus(delivery),
    receivingStatus,
    lines: (delivery.lines ?? []).map(mapDeliveryLine),
    qrShownAt: delivery.qr_shown_at,
    receivingDraftAt: delivery.receiving_draft_at,
  }
}

function mapReturnPickup(doc: ApiReturnPickup): ReturnPickupDoc {
  return {
    id: doc.id,
    returnCode: doc.return_code ?? doc.id,
    storeName: doc.store_name,
    itemCount: doc.item_count,
    destination: doc.destination,
    status: doc.status,
    pickedUpAt: doc.picked_up_at,
    lines: (doc.lines ?? []).map((l) => ({
      productName: l.product_name,
      productionDate: l.production_date,
      qty: l.qty,
    })),
  }
}

export function mapApiTripToRecord(api: ApiDeliveryTrip): TripRecord {
  const deliveries = api.deliveries ?? []
  return {
    id: api.id,
    delmanName: api.delman_name,
    kitchenName: api.kitchen_name,
    date: api.date,
    status: api.status,
    stops: deliveries.map(mapDeliveryStop),
    returnPickups: (api.return_pickups ?? []).map(mapReturnPickup),
    startedAt: api.started_at,
    startedViaStopId: api.started_via_delivery_id,
    completedAt: api.completed_at,
    transitRemainderQty: api.transit_remainder_qty,
  }
}

/** Sesi masih perlu dikerjakan delman (belum ditutup). */
export function isActiveApiSession(api: ApiDeliveryTrip): boolean {
  return (api.deliveries ?? []).length > 0 && api.status !== 'completed'
}

export function pickActiveApiSession(items: ApiDeliveryTrip[]): ApiDeliveryTrip | null {
  return items.find(isActiveApiSession) ?? null
}

export function mapCompletedSessionsToday(items: ApiDeliveryTrip[]): TripRecord[] {
  return items
    .filter((item) => (item.deliveries ?? []).length > 0 && item.status === 'completed')
    .map(mapApiTripToRecord)
    .sort((a, b) => {
      const at = a.completedAt ? new Date(a.completedAt).getTime() : 0
      const bt = b.completedAt ? new Date(b.completedAt).getTime() : 0
      return at - bt
    })
}

export function createWaitingSession(device: {
  delmanName: string
  kitchenName: string
  date: string
}): TripRecord {
  return {
    id: `WAIT-${device.date}`,
    delmanName: device.delmanName,
    kitchenName: device.kitchenName,
    date: device.date,
    status: 'ready',
    stops: [],
    returnPickups: [],
  }
}

export async function fetchTodaySessionsFromApi(
  delmanId: string,
  date: string,
): Promise<ApiDeliveryTrip[]> {
  if (shouldUseDelmanMock()) return []

  const result = await apiFetch<{ items: ApiDeliveryTrip[] }>(
    `/delivery-trips?delman_id=${encodeURIComponent(delmanId)}&date=${encodeURIComponent(date)}`,
  )
  return result.items
}

export async function fetchTodayTripFromApi(
  delmanId: string,
  date: string,
): Promise<TripRecord | null> {
  const items = await fetchTodaySessionsFromApi(delmanId, date)
  const active = pickActiveApiSession(items)
  return active ? mapApiTripToRecord(active) : null
}

export async function fetchTripDetailFromApi(tripId: string): Promise<TripRecord> {
  const detail = await apiFetch<ApiDeliveryTrip>(`/delivery-trips/${encodeURIComponent(tripId)}`)
  return mapApiTripToRecord(detail)
}

/**
 * Memuat sesi aktif hari ini + riwayat sesi yang sudah ditutup.
 * Antara sesi: active = sesi kosong (ready) menunggu perintah kirim berikutnya.
 */
export async function loadTodaySessions(device: {
  delmanId: string
  delmanName: string
  kitchenName: string
}): Promise<TodaySessionsLoad> {
  const date = todayDateKey()
  const waiting = createWaitingSession({
    delmanName: device.delmanName,
    kitchenName: device.kitchenName,
    date,
  })

  if (shouldUseDelmanMock()) {
    const fallback = getOrCreateTodayTrip(device.delmanName, device.kitchenName)
    const active =
      fallback.status !== 'completed' && fallback.stops.length > 0 ? fallback : waiting
    return { active, completedToday: [] }
  }

  try {
    const items = await fetchTodaySessionsFromApi(device.delmanId, date)
    const completedToday = mapCompletedSessionsToday(items)
    const activeApi = pickActiveApiSession(items)
    if (activeApi) {
      const active = mapApiTripToRecord(activeApi)
      saveTripRecord(active)
      return { active, completedToday }
    }
    saveTripRecord(waiting)
    return { active: waiting, completedToday }
  } catch {
    saveTripRecord(waiting)
    return { active: waiting, completedToday: [], apiError: true }
  }
}

/**
 * Memuat sesi aktif hari ini, mengutamakan data API (DB).
 */
export async function loadTodayTripPreferApi(device: {
  delmanId: string
  delmanName: string
  kitchenName: string
}): Promise<TripRecord> {
  const { active } = await loadTodaySessions(device)
  return active
}
